const GLOBAL_ERROR_REPORTER_INSTALLED = Symbol.for(
  "layer-nine.global-error-reporter.installed",
);

type ReporterWindow = Window & Record<symbol, boolean | undefined>;

function messageFrom(error: unknown): string {
  if (error instanceof Error) return error.message;
  if (typeof error === "string") return error;
  return "Unknown error";
}

function isResizeObserverLoopNoise(message: string): boolean {
  // Per the AppError Spine plan, this benign browser artifact has no stack and
  // is re-reported on the next frame.
  return /^ResizeObserver loop\b/i.test(message);
}

function stackFrom(error: unknown): string | null {
  if (error instanceof Error) return error.stack ?? null;
  if (
    typeof error === "object"
    && error !== null
    && "stack" in error
    && typeof error.stack === "string"
  ) return error.stack;
  return null;
}

function logGlobalError(
  source: "window.onerror" | "window.error" | "window.unhandledrejection",
  details: Record<string, unknown>,
): void {
  console.error(`[global-error] ${source}`, details);
}

/**
 * Log uncaught browser failures without changing their default handling.
 * The symbol guard keeps HMR and duplicate bootstrap imports from adding
 * duplicate listeners.
 */
export function installGlobalErrorReporter(): void {
  if (typeof window === "undefined") return;

  const reporterWindow = window as unknown as ReporterWindow;
  if (reporterWindow[GLOBAL_ERROR_REPORTER_INSTALLED]) return;
  reporterWindow[GLOBAL_ERROR_REPORTER_INSTALLED] = true;

  const previousOnError = window.onerror;
  window.onerror = (message, source, lineno, colno, error) => {
    const reportedError = error ?? message;
    const reportedMessage = typeof message === "string" ? message : messageFrom(reportedError);
    if (!isResizeObserverLoopNoise(reportedMessage)) {
      logGlobalError("window.onerror", {
        message: reportedMessage,
        stack: stackFrom(reportedError),
        reason: error ?? null,
        source: source ?? null,
        line: lineno ?? null,
        column: colno ?? null,
      });
    }
    return previousOnError?.call(window, message, source, lineno, colno, error) ?? false;
  };

  window.addEventListener("error", (event: ErrorEvent) => {
    const reportedError = event.error ?? event;
    const reportedMessage = event.message || messageFrom(reportedError);
    if (!isResizeObserverLoopNoise(reportedMessage)) {
      logGlobalError("window.error", {
        message: reportedMessage,
        stack: stackFrom(reportedError),
        reason: event.error ?? null,
        source: event.filename || null,
        line: event.lineno || null,
        column: event.colno || null,
      });
    }
  });

  window.addEventListener("unhandledrejection", (event: PromiseRejectionEvent) => {
    const reportedMessage = messageFrom(event.reason);
    if (!isResizeObserverLoopNoise(reportedMessage)) {
      logGlobalError("window.unhandledrejection", {
        message: reportedMessage,
        stack: stackFrom(event.reason),
        reason: event.reason,
        source: null,
        line: null,
        column: null,
      });
    }
  });
}
